import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { getAssetUrl } from "../../../api/api";
import { TCase } from "../../../types/types";
import Image from "../../image/image";
import CaseInfo from "../../main-page/cases-block/case-info/case-info";
import CaseTitle from "../../main-page/cases-block/case-title/case-title";
import TagList from "../../main-page/cases-block/tag-list/tag-list";
import "./case-item.sass";

type Props = {
  caseData: TCase;
  fillTagsFromCaseTags?: boolean;
  trimDescriptionToContent?: boolean;
};

const Case = ({
  caseData,
  fillTagsFromCaseTags,
  trimDescriptionToContent = false,
}: Props) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [displayText, setDisplayText] = useState<string>(
    caseData.description ?? "",
  );

  useEffect(() => {
    setDisplayText(caseData.description ?? "");
  }, [caseData.description]);

  useEffect(() => {
    if (!trimDescriptionToContent) {
      setDisplayText(caseData.description ?? "");
      return;
    }

    const textElement = textRef.current;
    if (!textElement) {
      return;
    }

    const measureElement = document.createElement("div");

    const updateDisplayedText = () => {
      const currentElement = textRef.current;
      if (!currentElement) {
        return;
      }

      const fullText = caseData.description ?? "";
      const computedStyle = window.getComputedStyle(currentElement);

      measureElement.style.position = "fixed";
      measureElement.style.visibility = "hidden";
      measureElement.style.pointerEvents = "none";
      measureElement.style.left = "-9999px";
      measureElement.style.top = "0";
      measureElement.style.zIndex = "-1";
      measureElement.style.boxSizing = computedStyle.boxSizing;
      measureElement.style.width = `${currentElement.clientWidth}px`;
      measureElement.style.padding = computedStyle.padding;
      measureElement.style.border = computedStyle.border;
      measureElement.style.fontFamily = computedStyle.fontFamily;
      measureElement.style.fontSize = computedStyle.fontSize;
      measureElement.style.fontWeight = computedStyle.fontWeight;
      measureElement.style.lineHeight = computedStyle.lineHeight;
      measureElement.style.letterSpacing = computedStyle.letterSpacing;
      measureElement.style.textTransform = computedStyle.textTransform;
      measureElement.style.whiteSpace = "normal";
      measureElement.style.wordBreak = computedStyle.wordBreak;
      measureElement.style.overflowWrap = computedStyle.overflowWrap;

      if (!measureElement.parentElement) {
        document.body.appendChild(measureElement);
      }

      const fits = (text: string) => {
        measureElement.textContent = text;
        return measureElement.scrollHeight <= currentElement.clientHeight + 1;
      };

      if (fits(fullText)) {
        setDisplayText((prev) => (prev === fullText ? prev : fullText));
        return;
      }

      let left = 0;
      let right = fullText.length;
      let best = "";

      while (left <= right) {
        const middle = Math.floor((left + right) / 2);
        const candidate = `${fullText.slice(0, middle).trimEnd()}...`;
        if (fits(candidate)) {
          best = candidate;
          left = middle + 1;
        } else {
          right = middle - 1;
        }
      }

      setDisplayText((prev) => (prev === best ? prev : best));
    };

    updateDisplayedText();
    const resizeObserver = new ResizeObserver(updateDisplayedText);
    resizeObserver.observe(textElement);
    if (textElement.parentElement) {
      resizeObserver.observe(textElement.parentElement);
    }
    window.addEventListener("resize", updateDisplayedText, { passive: true });

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateDisplayedText);
      measureElement.remove();
    };
  }, [caseData.description, trimDescriptionToContent]);

  const tagTitles =
    fillTagsFromCaseTags && !(caseData.tagsList?.length)
      ? (caseData.case_tags ?? []).map((t) => t.title)
      : (caseData.tagsList ?? []).map((t) => t.title);

  return (
    <div className="case caseItemAnchor">
      <div className="caseItemStackLayer">
        <Link to={`/cases/${caseData.slug}`} className="case-item">
        <div className="case-item__info">
          <CaseTitle title={caseData.title} />

          <div className="case-item__tags-slot">
            <TagList tagList={tagTitles} />
          </div>

          <div className="case-item__content">
            <div
              ref={textRef}
              className="case-item__text"
            >
              {displayText}
            </div>

            <div className="case-item__block-list">
              {caseData.previewStats &&
                caseData.previewStats.map((stat, index) => (
                  <CaseInfo
                    key={`${caseData.id}-${stat.id}-${index}`}
                    isSm={
                      index > 0 &&
                      (caseData.previewStats?.length ?? 0) > 2
                    }
                    groupValueAndText={
                      index === 0 &&
                      (caseData.previewStats?.length ?? 0) > 2
                    }
                    bgColor={
                      index === 0
                        ? stat.color && stat.color.hex
                        : "light-blue-bg"
                    }
                    textColor={index === 0 ? "white" : "blue"}
                    text={stat.description}
                    value={stat.number}
                  />
                ))}
            </div>
          </div>
        </div>

        <div className="case-item__img-wrap">
          <Image
            src={getAssetUrl(caseData.previewImage?.url)}
            alt={caseData.title}
            className="case-item__img"
          />
        </div>
      </Link>
      </div>
    </div>
  );
};

export default Case;
